/**
 * Motion math for the event-planner animation layer.
 *
 * Scene convention (see CoordinateService): +X = east, +Z = south, +Y = up.
 * Heading degrees: 0 = north (−Z), 90 = east (+X), 180 = south (+Z), 270 = west (−X).
 * So a forward unit vector for heading h is [sin(h), −cos(h)] in [x, z].
 */

export type EaseFn = (t: number) => number

/** Symmetric ease — smooth accel + decel. */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

/** Decelerate into the endpoint (fast start, gentle stop) — arrivals. */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

/** Accelerate away from the start (gentle start, fast end) — departures. */
export function easeInCubic(t: number): number {
  return t * t * t
}

export const EASES: Record<string, EaseFn> = {
  inOut: easeInOutCubic,
  out: easeOutCubic,
  in: easeInCubic,
  linear: (t) => t,
}

/** Shortest-path interpolation for angles in degrees (handles wraparound). */
export function lerpAngle(from: number, to: number, t: number): number {
  const delta = (((to - from) % 360) + 540) % 360 - 180
  return from + delta * t
}

/** Bearing in degrees from point a to point b, scene-feet [x, z]. */
export function bearingDeg(a: readonly [number, number], b: readonly [number, number]): number {
  const dx = b[0] - a[0]
  const dz = b[1] - a[1]
  // heading 0 = −Z (north), 90 = +X (east): atan2(dx, −dz)
  return (Math.atan2(dx, -dz) * 180) / Math.PI
}

/** Forward unit vector [x, z] for a heading in degrees. */
export function headingToForward(headingDeg: number): [number, number] {
  const r = (headingDeg * Math.PI) / 180
  return [Math.sin(r), -Math.cos(r)]
}

/** Cubic Bézier scalar evaluation (matches SweepPathRenderer). */
export function cubicBezier(a: number, b: number, c: number, d: number, t: number): number {
  const mt = 1 - t
  return mt * mt * mt * a + 3 * mt * mt * t * b + 3 * mt * t * t * c + t * t * t * d
}

export type Pt = [number, number]

/**
 * Arc-length-parameterized sample of a polyline (2+ points) at u ∈ [0,1].
 * Returns the position plus the local tangent bearing (degrees). For a 2-point
 * path this is a straight segment; richer waypoint paths just work.
 */
export function samplePolyline(points: readonly Pt[], u: number): { position: Pt; tangentDeg: number } {
  if (points.length === 0) return { position: [0, 0], tangentDeg: 0 }
  if (points.length === 1) return { position: [points[0][0], points[0][1]], tangentDeg: 0 }

  // Cumulative segment lengths.
  const seg: number[] = []
  let total = 0
  for (let i = 1; i < points.length; i++) {
    const dx = points[i][0] - points[i - 1][0]
    const dz = points[i][1] - points[i - 1][1]
    const len = Math.hypot(dx, dz)
    seg.push(len)
    total += len
  }
  if (total === 0) {
    return { position: [points[0][0], points[0][1]], tangentDeg: bearingDeg(points[0], points[points.length - 1]) }
  }

  const target = Math.max(0, Math.min(1, u)) * total
  let acc = 0
  for (let i = 0; i < seg.length; i++) {
    if (acc + seg[i] >= target || i === seg.length - 1) {
      const local = seg[i] === 0 ? 0 : (target - acc) / seg[i]
      const a = points[i]
      const b = points[i + 1]
      return {
        position: [a[0] + (b[0] - a[0]) * local, a[1] + (b[1] - a[1]) * local],
        tangentDeg: bearingDeg(a, b),
      }
    }
    acc += seg[i]
  }
  const last = points[points.length - 1]
  return { position: [last[0], last[1]], tangentDeg: bearingDeg(points[points.length - 2], last) }
}

/** Uniform Catmull-Rom interpolation between p1 and p2 (p0/p3 are neighbors). */
function catmullRom(p0: Pt, p1: Pt, p2: Pt, p3: Pt, t: number): Pt {
  const t2 = t * t
  const t3 = t2 * t
  const f = (a: number, b: number, c: number, d: number) =>
    0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3)
  return [f(p0[0], p1[0], p2[0], p3[0]), f(p0[1], p1[1], p2[1], p3[1])]
}

/** Clamp `q` so its perpendicular offset from the chord p1→p2 is ≤ maxDev. */
function clampToChord(q: Pt, p1: Pt, p2: Pt, maxDev: number): Pt {
  const vx = p2[0] - p1[0]
  const vz = p2[1] - p1[1]
  const len2 = vx * vx + vz * vz
  if (len2 < 1e-9) return q
  const t = ((q[0] - p1[0]) * vx + (q[1] - p1[1]) * vz) / len2
  const projX = p1[0] + t * vx
  const projZ = p1[1] + t * vz
  const dx = q[0] - projX
  const dz = q[1] - projZ
  const d = Math.hypot(dx, dz)
  if (d <= maxDev || d < 1e-9) return q
  const k = maxDev / d
  return [projX + dx * k, projZ + dz * k]
}

/**
 * Round the corners of a waypoint polyline with a Catmull-Rom spline so taxi
 * routes read as smooth turns rather than hard dog-legs. Returns a denser point
 * list that still passes through the original waypoints; `samplePolyline`
 * consumes it unchanged, so the motion leg model needs no changes.
 *
 * `maxDeviationFt` caps how far a smoothed sample may bow off the straight chord
 * between its two waypoints — so a sharp turn (e.g. leaving the runway onto a
 * taxiway) rounds gently but never cuts the corner across grass/off pavement.
 * Omit it to keep the unconstrained spline.
 */
export function smoothPath(points: readonly Pt[], samplesPerSeg = 8, maxDeviationFt?: number): Pt[] {
  if (points.length < 3) return points.map((p) => [p[0], p[1]] as Pt)
  const pts = points.map((p) => [p[0], p[1]] as Pt)
  const n = pts.length
  const out: Pt[] = []
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[Math.min(n - 1, i + 2)]
    for (let s = 0; s < samplesPerSeg; s++) {
      const q = catmullRom(p0, p1, p2, p3, s / samplesPerSeg)
      out.push(maxDeviationFt != null ? clampToChord(q, p1, p2, maxDeviationFt) : q)
    }
  }
  out.push([pts[n - 1][0], pts[n - 1][1]])
  return out
}
